/**
 * Map totals from sales-reports API response (see sales-reports-fe-api.md).
 * Do not sum paginated rows[] for KPI / footer totals.
 */

const num = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const sumRowsField = (rows, key) =>
  rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);

const distinctRowsCount = (rows, key) => {
  const set = new Set();
  rows.forEach((row) => {
    const value = row[key];
    if (value !== null && value !== undefined && value !== '') {
      set.add(value);
    }
  });
  return set.size;
};

/**
 * KPI cards: values only from API aggregate fields.
 */
export const buildTabKpisFromApi = (tabKey, ctx) => {
  const { summary: s, totals, totalRow, total, totalItems, rows = [] } = ctx;

  switch (tabKey) {
    case 'by-product':
      return {
        soLuong: num(s?.soLuong),
        doanhThu: num(s?.doanhThu),
        laiGop: num(s?.laiGop),
        count: num(s?.slSanPham) ?? totalItems,
      };
    case 'by-bill':
      return {
        count: num(s?.slHoaDon) ?? totalItems,
        soLuong: num(s?.soLuong),
        doanhThu: num(s?.doanhThu),
        laiGop: num(s?.laiGop),
      };
    case 'by-imei':
      return {
        count: num(s?.slImei) ?? totalItems,
        doanhThu: num(s?.doanhThu),
        giaBan: num(s?.giaBan) ?? num(s?.doanhThu),
        giaVon: num(s?.giaVon),
        giaNet: num(s?.giaNet),
        laiGop: num(s?.laiGop),
      };
    case 'purchase-by-imei':
      return {
        lineCount: num(s?.slDong) ?? num(s?.slImei) ?? totalItems,
        soLuong: num(s?.soLuong),
        giaTriNhap: num(s?.giaTriNhap) ?? num(s?.thanhTien),
        soNcc: num(s?.soNcc),
      };
    case 'by-bill-type': {
      const refundRow = rows.find((r) => Number(r.billType) === 3);
      return {
        slDon: num(totals?.slDon) ?? sumRowsField(rows, 'slDon'),
        doanhThu: num(totals?.doanhThu),
        laiGop: num(totals?.laiGop),
        traHang: num(refundRow?.doanhThu),
      };
    }
    case 'by-store-category':
      return {
        storeCount: num(ctx.storeCount),
        slDon: num(totalRow?.slDon),
        doanhThu: num(totalRow?.doanhThu),
        laiGop: num(totalRow?.laiGop),
      };
    case 'purchases':
      return {
        soLuong: num(s?.soLuong),
        thanhTienSauVat: num(s?.thanhTienSauVat),
        lineCount: totalItems,
      };
    case 'by-spc':
      return {
        soLuong: num(s?.soLuong),
        tongDoanhSo: num(s?.tongDoanhSo),
        tongLai: num(s?.tongLai),
        count: totalItems,
      };
    case 'by-customer':
      return {
        count: num(s?.slKhach) ?? totalItems,
        soLuong: num(s?.soLuong),
        doanhThu: num(s?.doanhThu),
        laiGop: num(s?.laiGop),
      };
    case 'by-customer-product':
      return {
        count: totalItems,
        soLuong: num(s?.soLuong),
        doanhThu: num(s?.doanhThu),
        laiGop: num(s?.laiGop),
      };
    case 'inventory-value-by-store':
      return {
        count: totalItems,
        slHangHoa: num(total?.slHangHoa),
        tongGiaTriTonKho: num(total?.tongGiaTriTonKho),
      };
    default:
      return {};
  }
};

/**
 * Footer row for tables — API totals only (by-bill-type: slDon/slHangHoa from full rows, max 4).
 */
export const buildTableTotalRowFromApi = (tabKey, ctx) => {
  const { summary: s, totals, totalRow, rows = [] } = ctx;

  if (tabKey === 'by-bill-type' && totals) {
    return {
      loaiHoaDon: 'TỔNG',
      slDon: sumRowsField(rows, 'slDon'),
      slHangHoa: sumRowsField(rows, 'slHangHoa'),
      doanhThu: totals.doanhThu,
      giaVon: sumRowsField(rows, 'giaVon'),
      laiGop: totals.laiGop,
      _isTotal: true,
    };
  }

  if (tabKey === 'by-customer' && s) {
    return {
      nhomKhach: '',
      maKhach: '',
      tenKhach: 'TỔNG',
      soLuong: s.soLuong,
      doanhThu: s.doanhThu,
      laiGop: s.laiGop,
      _isTotal: true,
    };
  }

  if (tabKey === 'by-store-category' && totalRow) {
    return {
      ...totalRow,
      maCuaHang: '',
      tenCuaHang: '',
      _isTotal: true,
    };
  }

  return null;
};
